import { isPassiveSupported } from '@/utils/functions/eventHandler/isPassiveSupported';
import { boundMethod } from 'autobind-decorator';
import { action, computed, observable, reaction } from 'mobx';
import { add, and, gte, has, lte, path, subtract } from 'ramda';
import React from 'react';

import { debounce, throttle } from '../utils';

export enum EN_SCROLL_Y_DIRECTION {
  UP = 'up',
  DOWN = 'down',
}

const isIncludes = (min: number, max: number, value: number): boolean => and(gte(value, min), lte(value, max));

const getScreenSize = (): { width: number; height: number } => {
  const body = document.getElementsByTagName('body')[0];
  const width = window.innerWidth || document.documentElement.clientWidth || body.clientWidth;
  const height = window.innerHeight || document.documentElement.clientHeight || body.clientHeight;

  return { width, height };
};

const getScrollOffset = (container: Document, target: HTMLElement | null): number => {
  const screen = getScreenSize();
  const body = document.getElementsByTagName('body')[0];
  const containerStyle = window.getComputedStyle(body);

  if (container === document) {
    return screen.height + (window.scrollY || window.pageYOffset);
  }

  if (!target) {
    return 0;
  }

  if (containerStyle.position === 'relative') {
    return target.offsetTop;
  }

  return target.offsetTop - container['offsetTop'];
};

class ScrollPos {
  @observable
  y: number = 0;
}

export class ScrollStore {
  @observable
  yDirection: EN_SCROLL_Y_DIRECTION = EN_SCROLL_Y_DIRECTION.DOWN;
  @observable
  isTop = true;
  @observable
  isBottom = false;

  readonly position = new ScrollPos();

  body: HTMLElement;
  originalPosition: {
    x: number;
    y: number;
  } | null;
  touchDown: boolean;
  collisionAdjust: number = 3;
  scrollTimerID: number;
  isBound: boolean = false;
  endReachedHandler: () => void;

  // 쓰로틀 걸린 핸들러 참조
  scrollHandler: () => void;
  touchmoveHandler: (event: TouchEvent & MouseEvent) => void;

  disposeEndReachedReaction: () => void;

  @computed
  get IsGoingDown(): boolean {
    return !this.isTop && this.yDirection === EN_SCROLL_Y_DIRECTION.UP;
  }

  @computed
  get IsGoingUp(): boolean {
    return !this.isBottom && this.yDirection === EN_SCROLL_Y_DIRECTION.DOWN;
  }

  @boundMethod
  bindEvent(elRef?: React.RefObject<HTMLElement> | React.MutableRefObject<HTMLElement | undefined>): void {
    if (window && !this.isBound) {
      const eventTarget = elRef?.current ? elRef.current : window;
      const isPassiveSupportedOnEventTarget = isPassiveSupported(eventTarget);
      this.scrollHandler = throttle(this.onScroll, 30, { leading: true, trailing: true });
      this.touchmoveHandler = throttle(this.onTouchMove, 10, { leading: true, trailing: true });

      eventTarget.addEventListener('scroll', this.scrollHandler, false);
      eventTarget.addEventListener('wheel', this.onWheel, isPassiveSupportedOnEventTarget ? { passive: true } : false);
      eventTarget.addEventListener(
        'touchstart',
        this.onTouchStart,
        isPassiveSupportedOnEventTarget ? { passive: true } : false,
      );
      eventTarget.addEventListener(
        'touchmove',
        this.touchmoveHandler,
        isPassiveSupportedOnEventTarget ? { passive: true } : false,
      );
      eventTarget.addEventListener('touchend', this.onTouchEnd, false);
      eventTarget.addEventListener('touchcancel', this.onTouchEnd, false);

      if (elRef?.current) {
        this.body = elRef.current;
      }

      this.isBound = true;
      this.updateIsEdge();
    }
  }

  @boundMethod
  unbindEvent(elRef?: React.RefObject<HTMLElement> | React.MutableRefObject<HTMLElement | undefined>): void {
    if (window) {
      const eventTarget = elRef?.current ? elRef.current : window;

      eventTarget.removeEventListener('scroll', this.scrollHandler);
      eventTarget.removeEventListener('wheel', this.onWheel);
      eventTarget.removeEventListener('touchstart', this.onTouchStart);
      eventTarget.removeEventListener('touchmove', this.touchmoveHandler);
      eventTarget.removeEventListener('touchend', this.onTouchEnd);
      eventTarget.removeEventListener('touchcancel', this.onTouchEnd);

      this.isBound = false;
    }
  }

  @boundMethod
  bindEndReachedEvent(element: React.RefObject<HTMLElement>, callback: () => void): void {
    if (window) {
      let previousePositionY = this.position.y;
      this.endReachedHandler = debounce(() => {
        // position.y가 변경 안된 경우 로직 처리 안함
        // mobx reaction으로 호출되는 케이스 이외에도 직접 호출하는 케이스가 있어서 추가
        if (previousePositionY === this.position.y) {
          return;
        }
        previousePositionY = this.position.y;
        const documentHeight = Math.max(document.body.scrollHeight, document.body.offsetHeight);
        const offset = Math.floor(documentHeight / 6);

        const el = element.current;
        const scrollTop = getScrollOffset(window.document, el) + offset;
        const isEndReached = documentHeight <= scrollTop;
        if (isEndReached) {
          callback();
        }
      }, 30);

      this.disposeEndReachedReaction = reaction(() => this.position.y, this.endReachedHandler);
    }
  }

  @boundMethod
  unbindEndReachedEvent(): void {
    if (window && this.disposeEndReachedReaction) {
      this.disposeEndReachedReaction();
    }
  }

  @action.bound
  updateIsEdge(): number {
    const innerHeight = this.body?.clientHeight ?? window.innerHeight;
    const scrollHeight = this.body?.scrollHeight ?? document.body.scrollHeight;
    const scrollTop = this.body?.scrollTop ?? (document.documentElement?.scrollTop || document.body.scrollTop);

    this.isBottom = scrollHeight - innerHeight - scrollTop <= 1;
    this.isTop = scrollTop <= 1;

    if (this.isTop) {
      this.position.y = 0;
    }

    this.position.y = scrollTop;

    return scrollTop;
  }

  @action.bound
  onScroll(): void {
    this.updateIsEdge();

    // detect scrolling stop event
    window.clearTimeout(this.scrollTimerID);
    this.scrollTimerID = setTimeout(() => {
      if (this.endReachedHandler) {
        this.endReachedHandler();
      }
    }, 90) as any;
  }

  @action.bound
  onTouchStart(event: TouchEvent & MouseEvent): void {
    const coord = this.getTouchCoord(event);
    this.touchDown = true;
    this.originalPosition = coord && {
      x: coord.x,
      y: coord.y,
    };
  }

  @action.bound
  onTouchEnd(event: TouchEvent & MouseEvent): void {
    const coord = this.getTouchCoord(event);

    if (this.touchDown && coord && !this.isTouchCollision(coord.x, coord.y)) {
      event.preventDefault();
    }

    this.touchDown = false;
    this.originalPosition = null;

    this.onScroll();
  }

  @action.bound
  onTouchMove(event: TouchEvent & MouseEvent): void {
    const info = this.getTouchInfo(event);

    if (!this.touchDown || !info) {
      return;
    }

    switch (info.direction.y) {
      case 'up':
        if (info.offset.y < -10) {
          this.yDirection = EN_SCROLL_Y_DIRECTION.UP;
        }
        break;
      case 'down':
        this.yDirection = EN_SCROLL_Y_DIRECTION.DOWN;
        break;
    }

    this.onScroll();
  }

  @action.bound
  onWheel(event: WheelEvent): void {
    let delta: number;

    if ((event as any).wheelDelta) {
      delta = (event as any).wheelDelta;
    } else {
      delta = event.deltaY * -1;
    }

    if (Math.abs(delta) > 20) {
      if (delta < 0) {
        this.yDirection = EN_SCROLL_Y_DIRECTION.UP;
      } else {
        this.yDirection = EN_SCROLL_Y_DIRECTION.DOWN;
      }
    }

    if (event.currentTarget && event.currentTarget !== window) {
      this.updateIsEdge();
    }
  }

  private isTouchCollision(x: number, y: number): boolean {
    const xCollision = isIncludes(subtract(x, this.collisionAdjust), add(x, this.collisionAdjust), x);
    const yCollision = isIncludes(subtract(y, this.collisionAdjust), add(y, this.collisionAdjust), y);
    return and(xCollision, yCollision);
  }

  private getTouchCoord(event: TouchEvent & MouseEvent): { x: number; y: number } | null {
    if (has('pageX', event)) {
      return {
        x: event.pageX,
        y: event.pageY,
      };
    }

    const pageX = path<number>(['changedTouches', '0', 'pageX'], event);
    const pageY = path<number>(['changedTouches', '0', 'pageY'], event);

    if (!pageX || !pageY) {
      return null;
    }

    return {
      x: pageX,
      y: pageY,
    };
  }

  private getTouchInfo(
    event: TouchEvent & MouseEvent,
  ): { direction: { x: string; y: string }; offset: { x: number; y: number } } | undefined {
    const coord = this.getTouchCoord(event);

    if (!this.originalPosition || !coord) {
      return;
    }

    const { x, y } = coord;

    const dx = x > this.originalPosition.x ? 'right' : 'left';
    const dy = y - 20 > this.originalPosition.y ? 'down' : 'up';

    return {
      direction: {
        x: dx,
        y: dy,
      },
      offset: {
        x: x - this.originalPosition.x,
        y: y - this.originalPosition.y,
      },
    };
  }
}

export default ScrollStore;

export const ScrollStoreCtx = React.createContext(new ScrollStore());
